import { styled, Theme } from '@mui/material/styles';
import { DataGrid, DataGridProps } from '@mui/x-data-grid';

/**
 * StyledDataGrid with theme-v2 CSS custom properties
 * This allows the DataGrid to automatically respect light/dark theme toggle
 */
export const StyledDataGrid = styled(DataGrid)<DataGridProps & { expandedFilters?: boolean; theme?: Theme }>(
  ({ expandedFilters }) => ({
    '--unstable_DataGrid-radius': '8px',

    // Base container styling
    backgroundColor: 'var(--sk-surface-default)',
    border: '1px solid var(--sk-surface-border)',
    borderRadius: 'var(--sk-radius-lg)',
    fontFamily: 'var(--sk-font-body)',
    color: 'var(--sk-text-primary)',

    // Override left border when filters are expanded
    borderLeft: expandedFilters ? '1px solid var(--sk-surface-border)' : '1px solid var(--sk-surface-border)',

    // Column headers
    '& .MuiDataGrid-columnHeaders': {
      backgroundColor: 'var(--sk-bg-secondary)',
      borderBottom: '1px solid var(--sk-surface-border)',
      minHeight: '48px !important',
      maxHeight: '48px !important',
    },
    '& .MuiDataGrid-columnHeader': {
      backgroundColor: 'var(--sk-bg-secondary)',
      '&:focus, &:focus-within': {
        outline: 'none',
      },
    },
    '& .MuiDataGrid-columnHeaderTitle': {
      fontFamily: 'var(--sk-font-display)',
      fontWeight: 600,
      fontSize: 'var(--sk-text-xs)',
      color: 'var(--sk-text-tertiary)',
      textTransform: 'uppercase',
      letterSpacing: '0.08em',
    },
    '& .MuiDataGrid-columnSeparator': {
      color: 'var(--sk-surface-border)',
    },

    // Cell styling
    '& .MuiDataGrid-cell': {
      borderBottom: '1px solid var(--sk-surface-border)',
      padding: '0 var(--sk-space-4)',
      color: 'var(--sk-text-primary)',
      fontSize: 'var(--sk-text-sm)',
      '&:focus, &:focus-within': {
        outline: 'none',
      },
    },

    // Row styling
    '& .MuiDataGrid-row': {
      backgroundColor: 'var(--sk-surface-default)',
      transition: 'background-color 0.2s ease',
      '&:hover': {
        backgroundColor: 'var(--sk-surface-hover)',
      },
      '&.Mui-selected': {
        backgroundColor: 'rgba(0, 200, 224, 0.1)',
        '&:hover': {
          backgroundColor: 'rgba(0, 200, 224, 0.15)',
        },
      },
      '&.purchased': {
        backgroundColor: 'rgba(0, 200, 224, 0.08)',
      },
    },

    // Virtual scroller (main content area)
    '& .MuiDataGrid-virtualScroller': {
      minHeight: '200px',
      backgroundColor: 'var(--sk-surface-default)',
    },
    '& .MuiDataGrid-virtualScrollerContent': {
      backgroundColor: 'var(--sk-surface-default)',
    },
    '& .MuiDataGrid-virtualScrollerRenderZone': {
      backgroundColor: 'var(--sk-surface-default)',
    },

    // Footer / Pagination
    '& .MuiDataGrid-footerContainer': {
      backgroundColor: 'var(--sk-bg-secondary)',
      borderTop: '1px solid var(--sk-surface-border)',
      minHeight: '48px',
    },
    '& .MuiTablePagination-root': {
      color: 'var(--sk-text-secondary)',
      fontFamily: 'var(--sk-font-body)',
    },
    '& .MuiTablePagination-selectLabel, & .MuiTablePagination-displayedRows': {
      color: 'var(--sk-text-secondary)',
      fontSize: 'var(--sk-text-sm)',
    },
    '& .MuiTablePagination-select': {
      color: 'var(--sk-text-primary)',
    },
    '& .MuiTablePagination-actions': {
      color: 'var(--sk-text-secondary)',
      '& .MuiIconButton-root': {
        color: 'var(--sk-text-secondary)',
        '&:hover': {
          backgroundColor: 'var(--sk-surface-hover)',
        },
        '&.Mui-disabled': {
          color: 'var(--sk-text-tertiary)',
        },
      },
    },

    // Checkbox styling
    '& .MuiCheckbox-root': {
      color: 'var(--sk-text-tertiary)',
      '&.Mui-checked': {
        color: 'var(--sk-cyan-500)',
      },
      '&:hover': {
        backgroundColor: 'rgba(0, 200, 224, 0.1)',
      },
    },

    // Scrollbar styling
    '& .MuiDataGrid-scrollbar': {
      '&::-webkit-scrollbar': {
        width: '8px',
        height: '8px',
      },
      '&::-webkit-scrollbar-track': {
        background: 'var(--sk-bg-secondary)',
      },
      '&::-webkit-scrollbar-thumb': {
        background: 'var(--sk-surface-border)',
        borderRadius: 'var(--sk-radius-full)',
        '&:hover': {
          background: 'var(--sk-surface-border-hover)',
        },
      },
    },

    // Loading overlay
    '& .MuiDataGrid-overlay': {
      backgroundColor: 'rgba(0, 0, 0, 0.5)',
      backdropFilter: 'blur(4px)',
    },
    '& .MuiLinearProgress-root': {
      backgroundColor: 'var(--sk-surface-border)',
    },
    '& .MuiLinearProgress-bar': {
      backgroundColor: 'var(--sk-cyan-500)',
    },

    // No rows overlay
    '& .MuiDataGrid-overlayWrapper': {
      minHeight: '200px',
    },

    // Icon buttons in header
    '& .MuiDataGrid-iconButtonContainer': {
      visibility: 'visible',
    },
    '& .MuiDataGrid-sortIcon': {
      color: 'var(--sk-text-tertiary)',
    },
    '& .MuiDataGrid-menuIcon': {
      color: 'var(--sk-text-tertiary)',
    },

    // Selected row count in footer
    '& .MuiDataGrid-selectedRowCount': {
      color: 'var(--sk-cyan-400)',
      fontFamily: 'var(--sk-font-mono)',
      fontSize: 'var(--sk-text-xs)',
    },
  }),
);
